import { createUserFromApi, } from 'api/model/user';
import { apiV2 } from '@newtontechnologies/beey-api-js-client/endpoints';
import { defaultUserSettings } from 'api/settings/user-settings';
import { BeeyLocale } from 'libs/locales/locale';
import { blob, json, } from '@newtontechnologies/beey-api-js-client/receivers';
import { catchUserError } from './users-api';
export const fetchTeam = async (connection, userId) => connection.fetch()
    .url(apiV2.admin.teams.id(userId).url())
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchTeamTranscriptions = async (connection, teamId, dates, orderBy, ascending, skip, count) => {
    const params = {
        skip,
        count,
        from: dates[0],
        to: dates[1],
        orderBy,
        ascending,
    };
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).transcribedPerPeriod.url(params))
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const addTeamCredit = async (connection, teamId, addedCredit) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).credit.add.url())
    .putJson({ credit: addedCredit })
    .receive(json()
    .catchHttpError((response) => (response.status === 412 ? { error: 'no-credit' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const removeTeamCredit = async (connection, teamId, removedCredit) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).credit.subtract.url())
    .putJson({ Credit: removedCredit })
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const exportTeamMembers = async (connection, teamId, dates, orderBy, ascending) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).exportMembers.url({
    from: dates[0],
    to: dates[1],
    orderBy,
    ascending,
}))
    .receive(blob())
    .fetch();
export const deleteTeam = async (connection, teamId) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).url())
    .delete()
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const setManagementGroup = async (connection, teamId, groupId) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).setManagementGroup.url())
    .postJson({
    ManagementGroupId: groupId,
})
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'invalid-group' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchSpeakerIdModels = async (connection, teamId) => {
    const models = await connection.fetch()
        .url(apiV2.admin.teams.id(teamId).voiceprintModels.url())
        .receive(json())
        .fetch();
    return models.map((model) => (Object.assign(Object.assign({}, model), { language: BeeyLocale.const(model.language) })));
};
export const deleteSpeakerIdModel = async (connection, teamId, locale) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).voiceprintModels.url({ language: locale.code }))
    .delete()
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const uploadSpeakerIdModel = async (connection, teamId, model, locale) => {
    const body = new FormData();
    body.append('Files', model);
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).voiceprintModels.url({ language: locale.code }))
        .put(body)
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const abandonProjectsIn = async (connection, teamId, abandonProjects) => {
    const body = {
        Age: abandonProjects.inDays === null ? null : `${String(abandonProjects.inDays)}.00:00:00`,
    };
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).abandonProjectsIn.url())
        .postJson(body)
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const addTeamMember = async (connection, email, role, tutorial, sendEmail, uiLocale, teamId) => {
    const settings = Object.assign(Object.assign({}, defaultUserSettings), { startBeeyTutorial: tutorial });
    const body = {
        Email: email.trim(),
        Role: role,
        Language: uiLocale.code,
        Settings: settings,
    };
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).members.url({ sendEmail }))
        .postJson(body)
        .receive(json()
        .map(createUserFromApi)
        .catchHttpError(catchUserError)
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const fetchStorageStatistics = async (connection, userId, userDataOnly) => connection.fetch()
    .url(apiV2.admin.teams.id(userId).storageStatistics.url({ userDataOnly }))
    .receive(json())
    .fetch();
export const fetchExportTemplates = async (connection, teamId) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).exportTemplates.url())
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const uploadExportTemplate = async (connection, teamId, name, template) => {
    const body = new FormData();
    body.append('Files', template);
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).exportTemplates.url({ Name: name }))
        .put(body)
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const deleteExportTemplate = async (connection, teamId, template) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).exportTemplates.url({ id: template }))
    .delete()
    .send();
export const fetchTeamPlan = async (connection, teamId) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).plan.url())
    .receive(json())
    .fetch();
export const fetchTeamSubscriptions = async (connection, teamId) => connection.fetch()
    .url(apiV2.admin.teams.id(teamId).subscriptions.url())
    .receive(json())
    .fetch();
export const transferOwnership = async (connection, teamId, newOwnerId) => {
    const body = { NewOwnerId: newOwnerId };
    return (connection.fetch()
        .url(apiV2.admin.teams.id(teamId).transferOwnership.url())
        .putJson(body)
        .send());
};
export const transferMemberToTeam = async (connection, teamId, memberId, newTeamId) => {
    const body = {
        MemberId: memberId,
        TargetTeamId: newTeamId,
    };
    return (connection.fetch()
        .url(apiV2.admin.teams.id(teamId).migrateMember.url())
        .putJson(body)
        .send());
};
export const setTeamExternalId = async (connection, teamId, externalId) => {
    return connection.fetch()
        .url(apiV2.admin.teams.id(teamId).setExternalId.url())
        .postJson({ ExternalId: externalId })
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
